import { Profile } from "~/models/profile";
import { ProfileCards } from "./ProfileCards";
import { Tabs } from "./Tabs";
import React from "react";
import { getProfilesInCategory } from "~/common/api/profile";
import Spinner from "~/common/icons/Spinner";
import { Logger } from "~/utils/logger";

const logger = new Logger("ProfileView", "info");

export const ProfileView = (props: {
  profiles: {
    tabTitle: string;
    category: { id: number; name: string; slug: string; taxonomy: string };
  }[];
}) => {
  const [activePage, setActivePage] = React.useState<Profile[] | null>(null);
  const [allPages, setAllPages] = React.useState<Profile[][]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  logger.debug("props:", props.profiles);

  const tabs = props.profiles.map((profile) => ({
    label: profile.tabTitle,
  }));

  // load profile data for the first tab on mount
  React.useEffect(() => {
    const loadTabs = async () => {
      // get the first page for the first tab and set it as the active page to show on load
      const firstTab = props.profiles[0].category;
      const page = await getProfilesInCategory(
        firstTab.taxonomy,
        firstTab.slug,
      ).catch((error) => {
        logger.error("Error fetching profile page:", error);
        setError("There was a problem loading the profile page.");
        return [];
      });
      logger.debug("tab item:", page);
      setActivePage(page);
      logger.debug("active page:", page);
      setLoaded(true);
      setAllPages([page]);

      // load the rest of the pages for the other tabs and store them in state so they're ready to go when the user clicks on the tab
      const otherPages = await Promise.all(
        props.profiles.slice(1).map((profile) =>
          getProfilesInCategory(
            profile.category.taxonomy,
            profile.category.slug,
          ).catch((error) => {
            logger.error("Error fetching profile page:", error);
            setError("There was a problem loading the profile page.");
            return [];
          }),
        ),
      );
      logger.debug("other pages:", otherPages);
      setAllPages((prev) => [...prev, ...otherPages]);
    };

    loadTabs();
  }, [props.profiles]);

  const [, setSelectedTabIndex] = React.useState(0);

  if (error) {
    return <div>{error}</div>;
  }

  if (!loaded) {
    return (
      <span className="tw:flex tw:flex-row tw:justify-center tw:items-center">
        <Spinner />
      </span>
    );
  }

  return (
    <>
      <Tabs
        tabData={tabs}
        onChange={(index) => {
          setSelectedTabIndex(index);
          setActivePage(allPages[index]);
        }}
      />
      <div className="tw:my-20">
        <ProfileCards profiles={activePage} />
      </div>
    </>
  );
};
